/**
 * Hook for UK address lookup using Ideal Postcodes API
 */
import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../services/api'

export interface UKAddress {
  line_1: string
  line_2?: string
  line_3?: string
  post_town: string
  postcode: string
  county?: string
  building_name?: string
  sub_building_name?: string
}

export interface AddressLookupResult {
  postcode: string
  addresses: UKAddress[]
}

/**
 * Hook for looking up UK addresses by postcode
 * @param enabled - Whether to execute the query (default: false, call trigger() to execute)
 */
export function useAddressLookup() {
  const [postcode, setPostcode] = useState<string>('')
  const [isEnabled, setIsEnabled] = useState(false)

  const query = useQuery({
    queryKey: ['address-lookup', postcode],
    queryFn: async () => {
      if (!postcode) {
        throw new Error('Postcode is required')
      }
      const response = await api.lookupAddress(postcode)

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to lookup address')
      }

      return response.data
    },
    enabled: isEnabled && !!postcode,
    retry: false, // Don't retry on failed postcode lookups
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false,
  })

  /**
   * Trigger address lookup for a given postcode
   */
  const lookup = useCallback((postcodeValue: string) => {
    const cleaned = postcodeValue.trim().toUpperCase()
    setPostcode(cleaned)
    setIsEnabled(true)
  }, [])

  /**
   * Reset the lookup state
   */
  const reset = useCallback(() => {
    setPostcode('')
    setIsEnabled(false)
  }, [])

  return {
    lookup,
    reset,
    data: query.data,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    isSuccess: query.isSuccess,
  }
}
